var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useImperativeHandle, memo } from 'react';
import classNames from 'classnames';
import Transition from '@helper/transition/Transition';
import PortalContainer from '@widget/popups/PortalContainer';
import { useOverlayStack } from '@widget/popups/overlay-stack';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import useMobileLayer, { useHasMobileShell } from '@core/layout/MobileLayer';
import MobileBackRow from '@core/layout/MobileBackRow';
const SlideInPanelBase = React.forwardRef((props, ref) => {
    const { children, isOpen, onClose, mode = 'full', direction = 'right', width, height, className, wrapperClassName, unmountOnExit = true, containerRef, borderRadius, beforeDismiss, title, } = props;
    const [containerBounds, setContainerBounds] = useState(null);
    // Run the optional dismiss guard, then close if it allows. Absent guard => close.
    const requestDismiss = (source) => __awaiter(void 0, void 0, void 0, function* () {
        if (!beforeDismiss) {
            onClose();
            return;
        }
        const allowed = yield beforeDismiss(source);
        if (allowed)
            onClose();
    });
    // Expose handlers to parent
    useImperativeHandle(ref, () => ({
        close: () => {
            onClose();
        },
    }));
    useEffect(() => {
        // If the consumer isn't in full + containerRef mode, drop the bounds.
        if (mode !== 'full' || !(containerRef === null || containerRef === void 0 ? void 0 : containerRef.current)) {
            setContainerBounds(null);
            return;
        }
        // While closing (isOpen=false) keep the last-known bounds so the exit
        // animation continues to play from the same position. Clearing them
        // here would re-class the wrapper to `--default` mid-animation and
        // the panel would slide from the wrong place.
        if (!isOpen) {
            return;
        }
        const updateBounds = () => {
            if (containerRef.current) {
                setContainerBounds(containerRef.current.getBoundingClientRect());
            }
        };
        updateBounds();
        const resizeObserver = new ResizeObserver(updateBounds);
        resizeObserver.observe(containerRef.current);
        window.addEventListener('resize', updateBounds);
        window.addEventListener('scroll', updateBounds);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateBounds);
            window.removeEventListener('scroll', updateBounds);
        };
    }, [containerRef, isOpen, mode]);
    // Escape dismiss — only when this panel is the TOPMOST overlay (overlay-stack):
    // stacked overlays each attach their own global listener and stopPropagation()
    // can't gate siblings on the same target, so without the check one Escape
    // would dismiss every open layer (e.g. a config panel AND the editor under it).
    // On phones, Back dismisses it like Escape (overlay-stack)
    // Phones with a title: the app header shows it and the footer Close bar closes the panel.
    // That layer then owns Back too, so the panel doesn't register it a second time.
    const isMobile = useIsMobileLayout();
    const hasShell = useHasMobileShell();
    const phoneTitle = isMobile && !!title;
    useMobileLayer(isOpen && phoneTitle && hasShell, { title, onClose: () => requestDismiss('escape') });
    const isTopOverlay = useOverlayStack(isOpen, phoneTitle && hasShell ? undefined : () => requestDismiss('escape'));
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key !== 'Escape')
                return;
            if (e.defaultPrevented)
                return; // a closer layer already handled it
            if (!isTopOverlay())
                return; // another overlay is on top
            e.preventDefault();
            e.stopPropagation();
            requestDismiss('escape');
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, onClose, beforeDismiss]);
    const isModal = mode === 'modal';
    const animationClassName = isModal
        ? 'uxpcore_slide_panel__animation_modal'
        : `uxpcore_slide_panel__animation_${direction}`;
    // Full mode: wrapper fills the content area or the containerRef bounds
    const fullWrapperStyle = containerBounds ? {
        position: 'fixed',
        top: containerBounds.top,
        left: containerBounds.left,
        width: containerBounds.width,
        height: containerBounds.height,
        zIndex: 999,
    } : {};
    // Modal mode: explicit width/height if provided
    const modalWrapperStyle = Object.assign(Object.assign({}, (width ? { width } : {})), (height ? { height } : {}));
    // Optional border-radius override. Set both `borderRadius` and the
    // logical start-start property because the SCSS sets the latter
    // explicitly — without overriding it the corner would still inherit
    // the SCSS default.
    const radiusStyle = borderRadius !== undefined
        ? { borderRadius, borderStartStartRadius: borderRadius }
        : {};
    const wrapperStyle = Object.assign(Object.assign({}, (isModal ? modalWrapperStyle : fullWrapperStyle)), radiusStyle);
    const wrapperClass = classNames('uxpcore_slide_panel__wrapper', isModal
        ? 'uxpcore_slide_panel__wrapper--modal'
        : {
            'uxpcore_slide_panel__wrapper--positioned': !!containerBounds,
            'uxpcore_slide_panel__wrapper--default': !containerBounds,
            // Phones: stops above the footer, where the Close bar shows
            'uxpcore_slide_panel__wrapper--layer': phoneTitle && hasShell,
        }, wrapperClassName);
    const backdropClass = classNames('uxpcore_slide_panel__backdrop', { 'uxpcore_slide_panel__backdrop--modal': isModal });
    return (_jsx(Transition, { in: isOpen, duration: 300, className: animationClassName, unmountOnExit: unmountOnExit, children: _jsx(PortalContainer, { hasBackdrop: true, onClickBackdrop: () => requestDismiss('backdrop'), className: backdropClass, children: _jsx("div", { className: wrapperClass, style: wrapperStyle, children: _jsxs("div", { className: classNames("uxpcore_slide_panel__content", className), children: [phoneTitle && !hasShell && _jsx(MobileBackRow, { title: title, onBack: () => requestDismiss('escape') }), children] }) }) }) }));
});
/**
 * @export
 * A component that renders a slide-in panel with animated transitions and a backdrop.
 * Supports two modes:
 * - `full`: fills the main content area (default)
 * - `modal`: centered overlay, auto-sized to content
 *
 * @example
 * ```
 * // Full mode (default)
 * <SlideInPanel isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <div>Panel Content</div>
 * </SlideInPanel>
 * ```
 *
 * @example
 * ```
 * // Modal mode — auto-sized
 * <SlideInPanel mode="modal" isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <div>Modal Content</div>
 * </SlideInPanel>
 * ```
 *
 * @example
 * ```
 * // Modal mode — explicit size
 * <SlideInPanel mode="modal" width="500px" height="400px" isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <div>Modal Content</div>
 * </SlideInPanel>
 * ```
 *
 * @example
 * ```
 * // Full mode with containerRef
 * const containerRef = useRef<HTMLDivElement>(null);
 * const panelRef = useRef<SlideInPanelHandlers>(null);
 *
 * <div ref={containerRef} style={{ width: 400, height: 300 }}>
 *   Container content
 * </div>
 *
 * <SlideInPanel
 *   isOpen={true}
 *   onClose={() => console.log('Closed')}
 *   containerRef={containerRef}
 *   ref={panelRef}
 * >
 *   <div>Panel Content</div>
 * </SlideInPanel>
 * ```
 *
 * @category Overlays
 */
export const SlideInPanel = memo(SlideInPanelBase);
